import { findDirective } from "./directives"

// ─── Types ──────────────────────────────────────────────────────────

export interface StateExpression {
  $state: string // JSON Pointer path e.g. "/revenue"
  $default?: unknown // fallback if state value is undefined
}

export interface BindStateExpression {
  $bindState: string // JSON Pointer path for two-way binding
}

export interface CondExpression {
  $cond: unknown // condition (can be expression or comparison)
  $then: unknown
  $else?: unknown
}

export interface TemplateExpression {
  $template: string // e.g. "Revenue for ${/region}: ${/revenue}"
}

export interface ComputedExpression {
  $computed: string // function name
  args?: Record<string, unknown> // args (resolved recursively)
}

export interface FilterExpression {
  $filter: unknown // source array (typically $state reference)
  where: Record<string, unknown> // filter conditions
}

// Comparison operators for $cond
export interface ComparisonExpression {
  eq?: [unknown, unknown]
  neq?: [unknown, unknown]
  gt?: [unknown, unknown]
  gte?: [unknown, unknown]
  lt?: [unknown, unknown]
  lte?: [unknown, unknown]
  not?: unknown
  $and?: unknown[]
  $or?: unknown[]
}

export type Expression =
  | StateExpression
  | BindStateExpression
  | CondExpression
  | TemplateExpression
  | ComputedExpression
  | FilterExpression

export interface ResolveContext {
  getState: (path: string) => unknown
  functions?: Record<string, (...args: unknown[]) => unknown>
  /**
   * Custom directive registry (e.g. `$format`, `$math`, `$truncate`).
   * Looked up *after* the built-in expressions ($state, $cond, …) so
   * built-ins always take precedence. See ./directives.ts.
   */

  directives?: import("./directives").DirectiveRegistry
  /**
   * Current repeat-scope item — set by Repeat element rendering. Enables
   * `{$item: "field"}` expressions inside the row template. `""` (empty
   * path) returns the whole item.
   */
  repeatItem?: unknown
  /** Current repeat-scope index. Enables `{$index: true}`. */
  repeatIndex?: number
}

export interface BindingInfo {
  path: string
  currentValue: unknown
}

// ─── Type Guards ────────────────────────────────────────────────────

export function isStateExpression(v: unknown): v is StateExpression {
  return isObj(v) && typeof (v as Record<string, unknown>).$state === "string"
}

export function isBindStateExpression(v: unknown): v is BindStateExpression {
  return isObj(v) && typeof (v as Record<string, unknown>).$bindState === "string"
}

export function isCondExpression(v: unknown): v is CondExpression {
  return (
    isObj(v) &&
    "$cond" in (v as Record<string, unknown>) &&
    "$then" in (v as Record<string, unknown>)
  )
}

export function isTemplateExpression(v: unknown): v is TemplateExpression {
  return isObj(v) && typeof (v as Record<string, unknown>).$template === "string"
}

export function isComputedExpression(v: unknown): v is ComputedExpression {
  return isObj(v) && typeof (v as Record<string, unknown>).$computed === "string"
}

export function isFilterExpression(v: unknown): v is FilterExpression {
  return (
    isObj(v) &&
    "$filter" in (v as Record<string, unknown>) &&
    "where" in (v as Record<string, unknown>)
  )
}

export function isExpression(v: unknown): v is Expression {
  return (
    isStateExpression(v) ||
    isBindStateExpression(v) ||
    isCondExpression(v) ||
    isTemplateExpression(v) ||
    isComputedExpression(v) ||
    isFilterExpression(v)
  )
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

// Track which $computed names we've already warned about to avoid
// console-flooding when the same broken function is referenced from
// many tiles or repeated rows in a Repeat.
const warnedComputedNames = new Set<string>()
function warnUnknownComputed(name: string): void {
  if (process.env.NODE_ENV === "production") return
  if (warnedComputedNames.has(name)) return
  warnedComputedNames.add(name)
  console.warn(
    "[dash] unknown $computed function: %o — register it via registerComputedFunction or fix the spec",
    name
  )
}

/**
 * Truthiness check used by `$and` / `$or` evaluation. Matches
 * JavaScript's standard truthiness with one exception: an empty array
 * counts as falsy here (consistent with how `$count: []` would behave).
 */
function isTruthy(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (value === false) return false
  if (value === 0) return false
  if (value === "") return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}

// ─── Resolution ─────────────────────────────────────────────────────

/**
 * Resolve a single value. If it's an expression, evaluate it.
 * If it's a plain object or array, recursively resolve all values.
 */
export function resolvePropValue(value: unknown, ctx: ResolveContext): unknown {
  if (value === null || value === undefined) return value

  if (isStateExpression(value)) {
    const result = ctx.getState(value.$state)
    return result !== undefined ? result : value.$default
  }

  if (isBindStateExpression(value)) {
    return ctx.getState(value.$bindState)
  }

  // Repeat-scope expressions. `$item` reads a path from the current
  // repeat item; `$index` returns the row index. Both are undefined
  // outside a Repeat (so misused expressions fall back gracefully
  // rather than crashing the render).
  if (
    isObj(value) &&
    "$item" in value &&
    typeof (value as Record<string, unknown>).$item === "string"
  ) {
    const path = (value as { $item: string }).$item
    if (ctx.repeatItem === undefined) return undefined
    return readItemPath(ctx.repeatItem, path)
  }
  if (isObj(value) && (value as Record<string, unknown>).$index === true) {
    return ctx.repeatIndex
  }

  // Boolean combinators. Used most often for visibility conditions
  // (`{$and: [...]}` / `{$or: [...]}`) but also legal anywhere a
  // boolean expression makes sense. Items are recursively resolved
  // and their truthiness combined; a single-key object yields
  // boolean — not a passthrough record.
  if (isObj(value) && Array.isArray((value as Record<string, unknown>).$and)) {
    const items = (value as { $and: unknown[] }).$and
    return items.every((item) => isTruthy(resolvePropValue(item, ctx)))
  }
  if (isObj(value) && Array.isArray((value as Record<string, unknown>).$or)) {
    const items = (value as { $or: unknown[] }).$or
    return items.some((item) => isTruthy(resolvePropValue(item, ctx)))
  }

  if (isCondExpression(value)) {
    const condition = resolvePropValue(value.$cond, ctx)
    const truthy = evaluateCondition(condition, ctx)
    return truthy ? resolvePropValue(value.$then, ctx) : resolvePropValue(value.$else, ctx)
  }

  if (isTemplateExpression(value)) {
    return value.$template.replace(/\$\{([^}]+)\}/g, (_, path: string) => {
      const resolved = ctx.getState(path.trim())
      return resolved !== undefined ? String(resolved) : ""
    })
  }

  if (isComputedExpression(value)) {
    const fn = ctx.functions?.[value.$computed]
    if (!fn) {
      // Unknown $computed name silently rendered as an empty string
      // before — surface in dev so the AI's typo is visible during
      // development. Rate-limited via warnedComputedNames so a list
      // of 50 rows referencing the same broken function only logs once.
      warnUnknownComputed(value.$computed)
      return undefined
    }
    const resolvedArgs: Record<string, unknown> = {}
    if (value.args) {
      for (const [k, v] of Object.entries(value.args)) {
        resolvedArgs[k] = resolvePropValue(v, ctx)
      }
    }
    return fn(resolvedArgs)
  }

  if (isFilterExpression(value)) {
    const source = resolvePropValue(value.$filter, ctx)
    if (!Array.isArray(source)) return source
    return source.filter((item) => {
      if (!isObj(item)) return true
      for (const [key, condition] of Object.entries(value.where)) {
        const resolvedCondition = resolvePropValue(condition, ctx)
        // "All" means no filter
        if (resolvedCondition === "All" || resolvedCondition === "all") continue
        const itemValue = item[key]
        if (isObj(resolvedCondition as unknown)) {
          const cond = resolvedCondition as Record<string, unknown>
          if ("gt" in cond && !(Number(itemValue) > Number(cond.gt))) return false
          if ("lt" in cond && !(Number(itemValue) < Number(cond.lt))) return false
          if ("gte" in cond && !(Number(itemValue) >= Number(cond.gte))) return false
          if ("lte" in cond && !(Number(itemValue) <= Number(cond.lte))) return false
          if ("includes" in cond && !String(itemValue).includes(String(cond.includes))) return false
        } else if (itemValue !== resolvedCondition) {
          return false
        }
      }
      return true
    })
  }

  // Recursively resolve arrays
  if (Array.isArray(value)) {
    return value.map((item) => resolvePropValue(item, ctx))
  }

  // Custom directive lookup ($format, $math, $truncate, $t, …).
  // Runs *after* every built-in expression check above so built-ins
  // always win. directives.ts imports only a type from this module
  // so the static cycle is type-only and safe.
  if (isObj(value) && ctx.directives) {
    const directive = findDirective(value, ctx.directives)
    if (directive) {
      return directive.resolve(value, ctx)
    }
  }

  // Recursively resolve plain objects
  if (isObj(value)) {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = resolvePropValue(v, ctx)
    }
    return result
  }

  return value
}

function evaluateCondition(condition: unknown, ctx: ResolveContext): boolean {
  if (
    condition === null ||
    condition === undefined ||
    condition === false ||
    condition === 0 ||
    condition === ""
  ) {
    return false
  }
  if (isObj(condition)) {
    const c = condition as Record<string, unknown>
    if ("eq" in c) {
      const [a, b] = c.eq as [unknown, unknown]
      return resolvePropValue(a, ctx) === resolvePropValue(b, ctx)
    }
    if ("neq" in c) {
      const [a, b] = c.neq as [unknown, unknown]
      return resolvePropValue(a, ctx) !== resolvePropValue(b, ctx)
    }
    if ("gt" in c) {
      const [a, b] = c.gt as [unknown, unknown]
      return Number(resolvePropValue(a, ctx)) > Number(resolvePropValue(b, ctx))
    }
    if ("gte" in c) {
      const [a, b] = c.gte as [unknown, unknown]
      return Number(resolvePropValue(a, ctx)) >= Number(resolvePropValue(b, ctx))
    }
    if ("lt" in c) {
      const [a, b] = c.lt as [unknown, unknown]
      return Number(resolvePropValue(a, ctx)) < Number(resolvePropValue(b, ctx))
    }
    if ("lte" in c) {
      const [a, b] = c.lte as [unknown, unknown]
      return Number(resolvePropValue(a, ctx)) <= Number(resolvePropValue(b, ctx))
    }
    if ("not" in c) {
      return !evaluateCondition(resolvePropValue(c.not, ctx), ctx)
    }
    if ("$and" in c) {
      return (c.$and as unknown[]).every((item) =>
        evaluateCondition(resolvePropValue(item, ctx), ctx)
      )
    }
    if ("$or" in c) {
      return (c.$or as unknown[]).some((item) =>
        evaluateCondition(resolvePropValue(item, ctx), ctx)
      )
    }
    // If it's a plain object with state expressions, resolve first
    if (isExpression(condition)) {
      return evaluateCondition(resolvePropValue(condition, ctx), ctx)
    }
  }
  return Boolean(condition)
}

/**
 * Deep-resolve all props in a dashboard element.
 */
export function resolveElement(
  element: Record<string, unknown>,
  ctx: ResolveContext
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(element)) {
    if (key === "type" || key === "stateKey") {
      resolved[key] = value
    } else if (key === "children" || key === "tabs" || key === "items") {
      resolved[key] = value // layout children resolved by their own renderer
    } else {
      resolved[key] = resolvePropValue(value, ctx)
    }
  }
  return resolved
}

/**
 * Extract $bindState metadata from an element for write-back callbacks.
 */
export function extractBindings(element: Record<string, unknown>): BindingInfo[] {
  const bindings: BindingInfo[] = []
  for (const [, value] of Object.entries(element)) {
    if (isBindStateExpression(value)) {
      bindings.push({ path: value.$bindState, currentValue: undefined })
    }
  }
  return bindings
}

/**
 * Collect every state path an element (or arbitrary value) references.
 * Used by `useResolvedElement` to memoise resolution against the
 * specific state slices the element actually reads — so a `setState`
 * write to an unrelated path doesn't re-resolve every tile on the
 * dashboard.
 *
 * Captured shapes (paths normalised to JSON Pointer form):
 *   {$state: "/x"} → "/x"
 *   {$bindState: "/x"} → "/x"
 *   {$filter: {$state: "/orders"}, ...} → "/orders" (recurses into source)
 *   {$template: "${/x} ${y}"} → "/x", "/y"
 *
 * Skipped:
 *   {$item}, {$index} — repeat-scope reads, not global state
 *   {$computed}, directive bodies — args are recursed but the function
 *     name itself isn't a path
 */
export function collectStatePaths(value: unknown, paths: Set<string> = new Set()): Set<string> {
  if (value === null || value === undefined) return paths

  if (isStateExpression(value)) {
    paths.add(value.$state)
    return paths
  }
  if (isBindStateExpression(value)) {
    paths.add(value.$bindState)
    return paths
  }
  if (isCondExpression(value)) {
    collectStatePaths(value.$cond, paths)
    collectStatePaths(value.$then, paths)
    if ("$else" in value) collectStatePaths((value as { $else: unknown }).$else, paths)
    return paths
  }
  if (isTemplateExpression(value)) {
    // Pull `${path}` references out of the template string. Bare
    // `${field}` (no leading slash) reads the same field from state
    // (per template semantics in resolvePropValue).
    for (const m of value.$template.matchAll(/\$\{([^}]+)\}/g)) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const ref = m[1]?.trim()
      if (!ref) continue
      paths.add(ref.startsWith("/") ? ref : `/${ref}`)
    }
    return paths
  }
  if (isComputedExpression(value)) {
    if (value.args) {
      for (const v of Object.values(value.args)) collectStatePaths(v, paths)
    }
    return paths
  }
  if (isFilterExpression(value)) {
    collectStatePaths(value.$filter, paths)
    for (const v of Object.values(value.where)) collectStatePaths(v, paths)
    return paths
  }
  // Boolean combinators — recurse into items so visibility conditions
  // built from $and / $or wire up the right state-slice deps.
  if (isObj(value) && Array.isArray((value as Record<string, unknown>).$and)) {
    for (const v of (value as { $and: unknown[] }).$and) collectStatePaths(v, paths)
    return paths
  }
  if (isObj(value) && Array.isArray((value as Record<string, unknown>).$or)) {
    for (const v of (value as { $or: unknown[] }).$or) collectStatePaths(v, paths)
    return paths
  }
  if (Array.isArray(value)) {
    for (const v of value) collectStatePaths(v, paths)
    return paths
  }
  if (isObj(value)) {
    for (const v of Object.values(value)) collectStatePaths(v, paths)
    return paths
  }
  return paths
}

/**
 * Read a JSON-Pointer-style path into a repeat-scope item. The empty
 * string `""` returns the whole item; otherwise the path is a slash-
 * delimited sequence of property names. Used by `{$item: "field"}`
 * expressions inside Repeat element bodies.
 */
export function readItemPath(item: unknown, path: string): unknown {
  if (path === "" || path === "/") return item
  const segments = path
    .replace(/^\//, "")
    .split("/")
    .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
  let current: unknown = item
  for (const seg of segments) {
    if (current && typeof current === "object" && !Array.isArray(current)) {
      current = (current as Record<string, unknown>)[seg]
    } else if (Array.isArray(current)) {
      const idx = Number(seg)
      if (Number.isInteger(idx)) current = current[idx]
      else return undefined
    } else {
      return undefined
    }
  }
  return current
}
